import { Component, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { Subject, takeUntil, distinctUntilChanged } from "rxjs";
import {
  IFleetPermission,
  IFleetPermissionModalData,
  IFleetPermissionModalResult,
  IFleetPermissionSave,
} from "@app/shared/data-access/src/lib/resource-management/models/fleet";
import { ResourceManagementEndpoints } from "@app/shared/data-access/src/lib/resource-management/types/resource-management-endpoints";
import { AdministrationEndpoints } from "@app/shared/data-access/src/lib/administration/types/administration-endpoints";
import {
  HardBtnComponent,
  HardNoDataComponent,
  HardTranslatePipe,
  DateFormatPipe,
} from "@app/shared/ui-lib/src/lib";
import { HardBaseFormControlComponent } from "@app/shared/ui-lib/src/lib/hard-form/hard-base-form-control/hard-base-form-control.component";
import { ConfirmationDialogService } from "@app/shared/ui-kit/dialogs/confirmation-dialog/services/confirmation-dialog.service";
import { IDicBase } from "@models/dictionary/dic-base";
import { DateFormatEnum } from "@models/core/date-format";
import { FleetPermissionModalComponent } from "./fleet-permission-modal/fleet-permission-modal.component";

@Component({
  standalone: true,
  selector: "app-fleet-permission-table",
  templateUrl: "./fleet-permission-table.component.html",
  styleUrl: "./fleet-permission-table.component.scss",
  imports: [
    CommonModule,
    MatDialogModule,
    HardBtnComponent,
    HardTranslatePipe,
    HardNoDataComponent,
    DateFormatPipe,
  ],
})
export class FleetPermissionTableComponent
  extends HardBaseFormControlComponent
  implements OnInit, OnDestroy
{
  permissions: IFleetPermission[] = [];
  private lastFleetOwnershipType: IDicBase | null = null;
  private readonly destroy$ = new Subject<void>();
  protected readonly dateFormatEnum = DateFormatEnum;

  constructor(
    private readonly dialog: MatDialog,
    private readonly confirmationDialog: ConfirmationDialogService,
  ) {
    super();
  }

  get isExternal(): boolean {
    return (
      this.form?.get("fleetOwnershipType")?.value?.code?.toLowerCase() ===
      "external_bp"
    );
  }

  get userDicType(): string {
    return this.isExternal
      ? `api/v1/${AdministrationEndpoints.BpUser}`
      : `api/v1/${AdministrationEndpoints.AppUser}`;
  }

  get manageTypeDicType(): string {
    return `api/v1/${ResourceManagementEndpoints.FleetManageType}`;
  }

  get canAddPermission(): boolean {
    return !!this.form?.get("fleetOwnershipType")?.value;
  }

  private isSameOwnershipType(
    a: IDicBase | Partial<IDicBase> | null | undefined,
    b: IDicBase | Partial<IDicBase> | null | undefined,
  ): boolean {
    if (a === b) return true;
    if (!a && !b) return true;
    if (!a || !b) return false;
    if (a.id && b.id) return a.id === b.id;
    if (a.code && b.code) return a.code === b.code;
    return false;
  }

  ngOnInit(): void {
    this.permissions =
      (this.form?.get("permissions")?.value as IFleetPermission[]) ?? [];
    this.lastFleetOwnershipType =
      (this.form?.get("fleetOwnershipType")?.value as IDicBase) ?? null;

    this.form
      ?.get("fleetOwnershipType")
      ?.valueChanges.pipe(
        distinctUntilChanged(
          (
            prev: IDicBase | Partial<IDicBase> | null,
            curr: IDicBase | Partial<IDicBase> | null,
          ) => this.isSameOwnershipType(prev, curr),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((newValue: IDicBase | null) => {
        if (this.isSameOwnershipType(newValue, this.lastFleetOwnershipType)) {
          return;
        }

        if (!this.permissions.length) {
          this.lastFleetOwnershipType = newValue;
          return;
        }

        const prevOwnershipType = this.lastFleetOwnershipType;

        this.confirmationDialog
          .openDialog({
            title: "base.confirm",
            message: "dicTable.fleetOwnershipTypeChangeConfirmationText",
            btnText: "base.ok",
          })
          .afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe((confirmed: boolean | undefined) => {
            if (confirmed) {
              this.permissions = [];
              this.syncToForm();
              this.lastFleetOwnershipType = newValue;
            } else {
              this.lastFleetOwnershipType = prevOwnershipType;
              this.form?.get("fleetOwnershipType")?.setValue(prevOwnershipType);
            }
          });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openAdd(): void {
    if (!this.canAddPermission) return;
    this.openModal(null);
  }

  openEdit(index: number): void {
    this.openModal(index);
  }

  private openModal(editingIndex: number | null): void {
    const permission =
      editingIndex !== null ? this.permissions[editingIndex] : undefined;
    const data: IFleetPermissionModalData = {
      permission,
      editingIndex: editingIndex ?? undefined,
      userDicType: this.userDicType,
      manageTypeDicType: this.manageTypeDicType,
    };

    const ref = this.dialog.open<
      FleetPermissionModalComponent,
      IFleetPermissionModalData,
      IFleetPermissionModalResult
    >(FleetPermissionModalComponent, {
      data,
      width: "520px",
    });

    ref
      .afterClosed()
      .subscribe((result: IFleetPermissionModalResult | undefined) => {
        if (!result) return;

        if (result.editingIndex !== null) {
          this.permissions = this.permissions.map((r, i) =>
            i === result.editingIndex ? result.permission : r,
          );
        } else {
          this.permissions = [...this.permissions, result.permission];
        }

        this.syncToForm();
      });
  }

  delete(index: number): void {
    this.permissions = this.permissions.filter((_, i) => i !== index);
    this.syncToForm();
  }

  private syncToForm(): void {
    const permissions: IFleetPermissionSave[] = this.permissions.map((r) => ({
      userId: r.user?.id,
      fleetManageTypeId: (r.fleetManageType?.id ??
        r.fleetManageTypeId) as string,
      expiresAt: r.expiresAt ?? null,
    }));
    this.form?.get("permissions")?.setValue(permissions);
  }
}
